import { Group, Stack } from '@mantine/core';
import { useDisclosure, useToggle } from '@mantine/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAgentSpending } from '../../api/financials-client/financials-client.hooks';
import { PageLayout } from '../../components/page-layout/page-layout';
import { useFeatureFlag } from '../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../helpers/feature-flags/feature-flags.type';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useFinancialsStore } from '../../store/financials-store/financials-store';
import { resetAllFinancialsFilters, setChart } from '../../store/financials-store/financials-store.actions';
import { Charts as ChartsType, State, View } from '../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../store/global-store/global-store';
import { baseWhite, inkLight, secondaryBase } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Divider } from '../../ui-library/divider/divider';
import { Icon } from '../../ui-library/icon/icon';
import { H2, Text } from '../../ui-library/typography/typography';
import { AgenticFinancialsSpendingTable } from '../agentic-process/agentic-financials/agentic-financials-spending-table';
import { useFlora } from '../flora/flora.context';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import { AgentCostsOverview } from './charts/agent-costs/overview';
import { ChartDataProvider } from './charts/chart-data-provider';
import { Charts } from './charts/charts';
import { Filters as ChartFilters } from './charts/filters';
import { Header } from './charts/header/header';
import { Overview as ChartsOverview } from './charts/overview';
import { Tabs } from './charts/tabs';
import { getFinancialFloraContext } from './financials.helpers';
import { usePortfolioProjectsAndBoards, useSpending } from './financials.hooks';
import { ByDate } from './table/by-date';
import { getAvailableSpendingYears } from './table/by-date.helpers';
import { BySprint } from './table/by-sprint';
import { Filters as TableFilters } from './table/filters';
import { Overview as TableOverview } from './table/overview';

export enum EditView {
  ByDate = 'By Date',
  BySprint = 'By Sprint',
}

export function Financials() {
  useDocumentTitle('Financials - Bloomfilter');
  const [view, setView] = useState<View>(View.Charts);
  const selectedChart = useFinancialsStore((state) => state.chart);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const startDate = useFinancialsStore((state: State) => state.startDate);
  const endDate = useFinancialsStore((state: State) => state.endDate);
  const team = useFinancialsStore((state: State) => state.team);
  const board = useFinancialsStore((state: State) => state.board);
  const { setContextualInfo } = useFlora();
  const [searchParams, setSearchParamsForTab] = useSearchParams();

  // Honor `?tab=agent` deep-links (e.g. from the Agent Miner page) by
  // switching the selected chart to Agent costs on mount, then strip the
  // param so it doesn't stick around for later renders / shared URLs.
  const didHandleTabParamRef = useRef(false);
  useEffect(() => {
    if (didHandleTabParamRef.current) return;
    if (searchParams.get('tab') === 'agent') {
      setChart(ChartsType.AgentCosts);
      didHandleTabParamRef.current = true;
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParamsForTab(next, { replace: true });
    }
  }, [searchParams, setSearchParamsForTab]);

  useEffect(() => {
    if (portfolio && startDate && endDate) {
      let selectedMeasures: string[] = [];
      switch (selectedChart) {
        case ChartsType.CostVersusOutput:
          selectedMeasures = ['cost_vs_output'];
          break;
        case ChartsType.AllocationOfCosts:
          selectedMeasures = ['cost_allocation'];
          break;
        case ChartsType.CostByInitiative:
          selectedMeasures = ['cost_by_initiative'];
          break;
        case ChartsType.AgentCosts:
          selectedMeasures = ['agent_costs'];
          break;
      }
      setContextualInfo(getFinancialFloraContext(portfolio, startDate, endDate, team, board, selectedMeasures));
    }
  }, [portfolio, startDate, endDate, team, board, selectedChart, setContextualInfo]);

  return view === View.Charts ? <FinancialsCharts setView={setView} /> : <FinancialsTable setView={setView} />;
}

function FinancialsCharts({ setView }: { setView: (view: View) => void }) {
  const { t } = useTranslation();
  const [isSideFilterOpen, { toggle: toggleSideFilter }] = useDisclosure(true);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const selectedChart = useFinancialsStore((state) => state.chart);
  const prevPortfolioIdRef = useRef<string | null>(portfolio?.id || null);
  const [headerKey, setHeaderKey] = useState<string>(portfolio?.id || 'no-portfolio');

  // Reset all financials filters when portfolio changes
  useEffect(() => {
    if (prevPortfolioIdRef.current !== null && portfolio?.id !== prevPortfolioIdRef.current) {
      // Portfolio has changed, reset all financials filters to ensure they're relevant to the new context
      resetAllFinancialsFilters();
      // After resetting filters, force remount the Header so its form initializes from fresh store values
      setHeaderKey(`${portfolio?.id || 'no-portfolio'}:${Date.now()}`);
    }

    prevPortfolioIdRef.current = portfolio?.id || null;
  }, [portfolio?.id]);

  return (
    <MenuDrivenContainer
      header={
        <Group justify="flex-start" style={{ width: '100%' }}>
          <Icon name="payments" color={secondaryBase} size={36} />
          <H2>{t('navigation.financials')}</H2>
        </Group>
      }
    >
      <ChartDataProvider>
        {({ agentCostsData, allocationOfCostsData, costByInitiativeData, costVersusOutputData, isPending }) => (
          <PageLayout
            controlBar={<Header key={headerKey} setView={setView} />}
            overview={{
              title: t('common.overview.title'),
              content:
                selectedChart === ChartsType.AgentCosts ? (
                  <AgentCostsOverview summary={agentCostsData?.summary} isPending={isPending} />
                ) : (
                  <ChartsOverview />
                ),
            }}
            tabs={<Tabs />}
            sideFilters={{
              title: t('common.filters'),
              content: (
                <ChartFilters
                  setShow={toggleSideFilter}
                  agentCostsData={agentCostsData}
                  allocationOfCostsData={allocationOfCostsData}
                  costByInitiativeData={costByInitiativeData}
                  isPending={isPending}
                />
              ),
              show: isSideFilterOpen,
              setShow: toggleSideFilter,
            }}
            mainSection={
              <Charts
                agentCostsData={agentCostsData}
                allocationOfCostsData={allocationOfCostsData}
                costByInitiativeData={costByInitiativeData}
                costVersusOutputData={costVersusOutputData}
                isPending={isPending}
              />
            }
          />
        )}
      </ChartDataProvider>
    </MenuDrivenContainer>
  );
}

function FinancialsTable({ setView }: { setView: (view: View) => void }) {
  const { t } = useTranslation();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const organization = useGlobalStore((state) => state.organization);
  const isAgenticProcessEnabled = useFeatureFlag(FeatureFlag.AgenticProcessByHooks);
  const [edit, toggle] = useToggle(Object.values(EditView));
  const [editTab, setEditTab] = useState<'project' | 'agent'>('project');
  const { data: spendingData, query: spendingQuery } = useSpending(portfolio?.id);
  // Use raw query data (not subproject-filtered) so all years appear in the By Date filter
  const availableYears = getAvailableSpendingYears(spendingQuery.data);

  const { data: projects } = usePortfolioProjectsAndBoards(portfolio?.id);

  // Agent costs data
  const [agentCostsYear, setAgentCostsYear] = useState<number>(new Date().getFullYear());
  const { data: agentSpending } = useAgentSpending({ organizationId: organization?.id ?? '' });
  const agentCostsAvailableYears = useMemo(
    () =>
      Array.from(new Set([...(agentSpending?.map((s) => s.year) || []), new Date().getFullYear()])).sort(
        (a, b) => b - a,
      ),
    [agentSpending],
  );
  const filteredAgentSpending = useMemo(
    () => agentSpending?.filter((s) => s.year === agentCostsYear) || [],
    [agentSpending, agentCostsYear],
  );

  const prevOrgIdRef = useRef(organization?.id);
  const prevPortfolioIdRef = useRef(portfolio?.id);

  useEffect(() => {
    const orgChanged = organization?.id !== prevOrgIdRef.current;
    const portfolioChanged = portfolio?.id !== prevPortfolioIdRef.current;

    if (orgChanged || portfolioChanged) {
      setView(View.Charts);
    }

    prevOrgIdRef.current = organization?.id;
    prevPortfolioIdRef.current = portfolio?.id;
  }, [organization?.id, portfolio?.id, setView]);

  const spendingDataByProject = (spendingData || []).filter((entry) =>
    projects?.some((p) => p.id === entry.project_id),
  );

  const isProjectCosts = editTab === 'project';

  return (
    <MenuDrivenContainer
      header={
        <Group justify="flex-start" style={{ width: '100%' }}>
          <Button
            variant="secondary"
            onClick={() => setView(View.Charts)}
            radius="xl"
            size="xs"
            leftSection={<Icon name="arrow_back" color={baseWhite} size={16} />}
          >
            {t('common.actions.back')}
          </Button>
          <H2>{t('financials.table.edit_financial_data')}</H2>
        </Group>
      }
    >
      <PageLayout
        controlBar={null}
        overview={
          isProjectCosts
            ? {
                title: t('common.overview.title'),
                content: <TableOverview spendingData={spendingDataByProject} editView={edit} />,
              }
            : undefined
        }
        tabs={
          isAgenticProcessEnabled ? (
            <Group>
              <Button
                variant={isProjectCosts ? 'secondary' : 'outline'}
                radius="xl"
                size="xs"
                leftSection={
                  <Icon
                    name="payments"
                    size={14}
                    color={isProjectCosts ? baseWhite : inkLight}
                    style={{ cursor: 'pointer' }}
                  />
                }
                onClick={() => setEditTab('project')}
              >
                {t('financials.table.project_costs')}
              </Button>
              <Button
                variant={!isProjectCosts ? 'secondary' : 'outline'}
                radius="xl"
                size="xs"
                leftSection={
                  <Icon
                    name="smart_toy"
                    size={14}
                    color={!isProjectCosts ? baseWhite : inkLight}
                    style={{ cursor: 'pointer' }}
                  />
                }
                onClick={() => setEditTab('agent')}
              >
                Agent Costs
              </Button>
            </Group>
          ) : undefined
        }
        sideFilters={
          isProjectCosts
            ? {
                title: t('common.filters'),
                content: <TableFilters availableYears={availableYears} editView={edit} setEditView={toggle} />,
                width: 250,
              }
            : {
                title: t('common.filters'),
                content: (
                  <Stack>
                    <Text weight="bold">Years</Text>
                    <Divider />
                    {agentCostsAvailableYears.map((year) => (
                      <Button
                        key={year}
                        variant={agentCostsYear === year ? 'secondary' : 'outline'}
                        radius="xl"
                        size="sm"
                        style={{ width: '70px' }}
                        onClick={() => setAgentCostsYear(year)}
                      >
                        {year}
                      </Button>
                    ))}
                  </Stack>
                ),
                width: 250,
              }
        }
        mainSection={
          isProjectCosts ? (
            edit === EditView.ByDate ? (
              <ByDate spendingData={spendingDataByProject} isLoading={spendingQuery.isFetching} />
            ) : (
              <BySprint />
            )
          ) : (
            <AgenticFinancialsSpendingTable
              key={`${organization?.id ?? 'no-org'}-${agentCostsYear}`}
              agentSpending={filteredAgentSpending}
              selectedYear={agentCostsYear}
            />
          )
        }
      />
    </MenuDrivenContainer>
  );
}
